import { SWRResponse } from "swr";
import { ApiSchemaCompany } from "../../models/api";
import { AxiosAuth } from "../axios";
import {
  useExtendedApiAccess,
  CreateAccessObject,
} from "./useExtendedApiAccess";
import { AppRouteApi } from "../../config/appRoute";
import { PaginatedData } from "../../models/api";
import { useAppSelector } from "../redux";
import { useRef, useEffect } from "react";
import { ApiResponseContainer } from "../../models/api";
import { ApiSchemaCompanyAllDetails } from "../../models/api/company";
import { useAppDispatch } from "../redux/ReduxStore";
import { ActionsCompanyTable } from "../redux/slices/sliceCompanyTable";
import { showErrorToast } from "../../components/toast.component";

const CacheIds = {
  GetPaginated: AppRouteApi.Company.Root(),
  ById: AppRouteApi.Company.ById(""),
  AllDetails: AppRouteApi.Company.AllDetails(),
  GetAll: AppRouteApi.Company.AllCompanies(),
};

function useApiCompanyGetAll(): SWRResponse<
  ApiSchemaCompany[] | undefined,
  Error
> {
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaCompany[]>(CacheIds.GetAll, () =>
    AxiosAuth.get(CacheIds.GetAll)
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsCompanyTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaCompany[]>
        >(CacheIds.GetAll);

        if (res.data.isSuccess) {
          await api.mutate(res.data.data, false);
        }
        dispatch(ActionsCompanyTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return api;
}

function useApiCompanyGetPaginated(): SWRResponse<
  PaginatedData<ApiSchemaCompany> | undefined,
  Error
> {
  const { pageNumber, pageSize, searchKey } = useAppSelector(
    (state) => state.companyTable
  );
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<PaginatedData<ApiSchemaCompany>>(
    CacheIds.GetPaginated,
    () =>
      AxiosAuth.get(CacheIds.GetPaginated, {
        params: {
          PageNumber: pageNumber,
          PageSize: pageSize,
          SearchKey: searchKey,
        },
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsCompanyTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<PaginatedData<ApiSchemaCompany>>
        >(CacheIds.GetPaginated, {
          params: {
            PageNumber: pageNumber,
            PageSize: pageSize,
            SearchKey: searchKey,
          },
        });

        if (res.data.isSuccess) {
          await api.mutate(res.data.data, false);
        }
        dispatch(ActionsCompanyTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, searchKey]);

  return api;
}

function useApiCompanyById(
  id: string
): SWRResponse<ApiSchemaCompany | undefined, Error> {
  return useExtendedApiAccess<ApiSchemaCompany>(CacheIds.ById, () =>
    AxiosAuth.get(AppRouteApi.Company.ById(id))
  );
}

function useApiCompanyAllDetails(
  companyId: string
): SWRResponse<ApiSchemaCompanyAllDetails | undefined, Error> {
  return useExtendedApiAccess<ApiSchemaCompanyAllDetails>(
    CacheIds.AllDetails,
    () =>
      AxiosAuth.get(AppRouteApi.Company.AllDetails(), { params: { companyId } })
  );
}

const AccessObject = {
  GetAll: CreateAccessObject<ApiSchemaCompany[], typeof useApiCompanyGetAll>(
    CacheIds.GetAll,
    useApiCompanyGetAll
  ),
  GetPaginated: CreateAccessObject<
    PaginatedData<ApiSchemaCompany>,
    typeof useApiCompanyGetPaginated
  >(CacheIds.GetPaginated, useApiCompanyGetPaginated),

  ById: CreateAccessObject<ApiSchemaCompany, typeof useApiCompanyById>(
    CacheIds.ById,
    useApiCompanyById
  ),

  AllDetails: CreateAccessObject<
    ApiSchemaCompanyAllDetails,
    typeof useApiCompanyAllDetails
  >(CacheIds.AllDetails, useApiCompanyAllDetails),
};

async function AddCompany(
  Company: FormData
): Promise<ApiResponseContainer<boolean>> {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<boolean>>(
      AppRouteApi.Company.Register(),
      Company
    );
    if (response.data.isSuccess) {
      return response.data;
    }
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return { isSuccess: false };
}

async function CompanyRegister(
  Company: FormData
): Promise<ApiResponseContainer<boolean>> {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<boolean>>(
      AppRouteApi.Company.Register(),
      Company
    );
    if (response.data.isSuccess) return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return { isSuccess: false };
}

async function GetAllCompany(): Promise<
  ApiResponseContainer<ApiSchemaCompany[]>
> {
  try {
    const response = await AxiosAuth.post<
      ApiResponseContainer<ApiSchemaCompany[]>
    >(AppRouteApi.Company.All());
    if (response.data.isSuccess) return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return { isSuccess: false };
}

async function UpdateCompany(
  Company: FormData
): Promise<ApiResponseContainer<ApiSchemaCompany>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaCompany>
    >(AppRouteApi.Company.Root(), Company);
    if (response.data.isSuccess) {
      const updAction = (
        res?: PaginatedData<ApiSchemaCompany>
      ): PaginatedData<ApiSchemaCompany> | undefined => {
        if (res && Array.isArray(res?.list) && response.data.data) {
          return {
            totalCount: res.totalCount + 1,
            list: [...res.list, response.data.data],
          };
        } else if (res) return res;
      };
      AccessObject.GetPaginated.Mutate(updAction());
      return response.data;
    }
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return { isSuccess: false };
}

async function DeleteCompany(ids: string[]): Promise<boolean> {
  try {
    const response = await AxiosAuth.delete<ApiResponseContainer<boolean>>(
      AppRouteApi.Company.Delete(),
      {
        data: ids,
      }
    );
    if (response.data.isSuccess) return true;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return false;
}

async function GetCompanyById(
  id: string
): Promise<ApiSchemaCompany | undefined> {
  try {
    const response = await AxiosAuth.get<
      ApiResponseContainer<ApiSchemaCompany>
    >(AppRouteApi.Company.ById(id));
    if (response.data.isSuccess) return response.data.data;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return undefined;
}

async function RequestSubscription() {
  try {
    const response = await AxiosAuth.get<ApiResponseContainer<boolean>>(
      AppRouteApi.Company.RequestSubscription()
    );
    if (response.data.isSuccess) return true;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return false;
}

async function ToggleGPSCheckIn(
  company: ApiSchemaCompany,
  isGpsCheckInEnable: boolean
): Promise<ApiResponseContainer<ApiSchemaCompany>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaCompany>
    >(AppRouteApi.Company.GPSCheckInToggle(), {
      id: company.sKey,
      isGpsCheckInEnable: isGpsCheckInEnable,
    });
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaCompany[]
      ): ApiSchemaCompany[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function ToggleAutoClockIn(
  company: ApiSchemaCompany,
  isAutoClockInEnable: boolean
): Promise<ApiResponseContainer<ApiSchemaCompany>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaCompany>
    >(AppRouteApi.Company.AutoClockCheckInToggle(), {
      id: company.sKey,
      isAutoClockInEnable: isAutoClockInEnable,
    });
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaCompany[]
      ): ApiSchemaCompany[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

export const ApiCompany = {
  GetAllCompanies: AccessObject.GetAll,
  ById: AccessObject.ById,
  GetPaginated: AccessObject.GetPaginated,
  AllDetails: AccessObject.AllDetails,
  GetAll: GetAllCompany,
  Add: AddCompany,
  Delete: DeleteCompany,
  Update: UpdateCompany,
  Register: CompanyRegister,
  GetCompanyById: GetCompanyById,
  RequestSubscription,
  ToggleGPSCheckIn,
  ToggleAutoClockIn,
};
